import React, {useImperativeHandle, forwardRef, useState, useEffect, useRef} from 'react';
import {
    Button,
    Dialog,
    Title,
    Flex,
    Icon,
    PriorityIcon
} from '@priority-software/priority-style-react';
import {endProc, selectProc, closeReportsDlg} from '../../../_shared/GWTBridge/invoke/procMain';
import styles from '../styles/ReportsDialog.module.scss';
import {getNextMaxDialogsZIndex} from '../../../_shared/GWTBridge/invoke/genPage';
import {selectedOptionsMap} from '../consts/reportOptions';
import {
    generateReportOptions,
    getFormats,
    getExcelTemplates,
    getSavedReports
} from '../utils/reportsDialog';
import texts from '../texts';
import {ReportDialogProps, ReportDialogOption} from '../types';
import {SavedReports} from './SavedReports';
import {approveReportDlg} from '../actions/reportsDialog';
import {featurePopoverRef} from '../../FeaturePopover/FeaturePopover.root';
import {ReportDialogContent} from './ReportDialogContent';
import {keyCodes} from '../../../constants';
import {noop} from '@priority-software/priority-style-react';

export const ReportsDialog = forwardRef((props: ReportDialogProps, ref: any) => {
    const dlgContentRef = useRef(null);
    const config = generateReportOptions(props);
    const formats = getFormats(props);
    const excelTemplates = getExcelTemplates(props);
    const savedReports = getSavedReports(props);

    const [dialogZIndex, setDialogZIndex] = useState(getNextMaxDialogsZIndex());
    const [selectedOption, setSelectedOption] = useState(
        config.find(
            item =>
                item.mode === selectedOptionsMap[props.optionselected] ||
                item.submode === selectedOptionsMap[props.optionselected]
        )
    );
    const [selectedFormat, setSelectedFormat] = useState(
        formats?.length ? formats.find(format => format.isSelected) : null
    );
    const [selectedExcelTemplate, setSelectedExcelTemplate] = useState(excelTemplates[0]);
    const [isPdf, setIsPdf] = useState(props.pdf === '1');
    const [isLockedExcel, setIsLockedExcel] = useState(
        selectedOptionsMap[props.optionselected] === selectedOptionsMap.protectedexcel
    );

    const [isFocused, setIsFocused] = useState(true);

    useEffect(() => {
        if (isFocused) {
            window.addEventListener('keydown', handleKeyDown);
            return () => window.removeEventListener('keydown', handleKeyDown);
        }
        return noop;
    }, [isFocused]);

    const handleKeyDown = e => {
        if (e.keyCode === keyCodes.ENTER) {
            e.stopPropagation();
            handleApprove();
        }
    };

    const handleCancelProc = () => {
        onClose();
        endProc();
    };

    const onClose = () => {
        closeReportsDlg();
        closeFeaturePopover();
    };

    const closeFeaturePopover = () => {
        featurePopoverRef?.current?.handleClose(true);
    };

    const handleApprove = async () => {
        const mode =
            selectedOption.mode === selectedOptionsMap.excel && isLockedExcel
                ? selectedOptionsMap.protectedexcel
                : selectedOption.mode;
        const format =
            (selectedOption.mode === selectedOptionsMap.report ||
                selectedOption.mode === selectedOptionsMap.file ||
                selectedOption.mode === selectedOptionsMap.table) &&
            selectedFormat
                ? selectedFormat.value
                : '0';
        await approveReportDlg(props.etype, props.ename, mode, format, props.zoomValue, isPdf);
        onClose();
    };

    const updateZIndex = (zIndex: number) => {
        setDialogZIndex(zIndex);
    };

    useEffect(() => {
        if (dialogZIndex < 0) {
            closeFeaturePopover();
        } else {
            focusDialog();
        }
    }, [dialogZIndex]);

    const focusDialog = () => {
        dlgContentRef?.current?.focus();
    };

    const handleMouseDown = () => {
        selectProc(props.elementId);
    };

    const getZIndex = (): number => {
        return dialogZIndex;
    };

    const chooseOption = (option: ReportDialogOption) => {
        setSelectedOption(option);
    };

    useImperativeHandle(
        ref,
        () => {
            return {
                updateZIndex: updateZIndex,
                getZIndex: getZIndex,
                closeFeaturePopover: closeFeaturePopover,
                focusReportDialog: focusDialog
            };
        },
        [dialogZIndex, getZIndex]
    );

    const renderFooter = () => {
        return (
            <Flex>
                {savedReports && (
                    <SavedReports
                        initSavedReports={savedReports}
                        dialogZIndex={dialogZIndex}
                        etype={props.etype}
                        type={props.type}
                        ename={props.ename}
                        closeDialog={handleCancelProc}
                    />
                )}
                <Button
                    onClick={handleApprove}
                    skin="primary"
                    width="regularFixed"
                    size="large"
                    disabled={selectedOption?.excelTemplates && selectedExcelTemplate?.isOld}
                >
                    {texts.approve}
                </Button>
            </Flex>
        );
    };

    const handleFocus = () => {
        setIsFocused(true);
    };

    const handleBlur = () => {
        setIsFocused(false);
    };

    return (
        <div onMouseDown={handleMouseDown} onFocus={handleFocus} onBlur={handleBlur}>
            <Dialog
                isOpen
                size="large"
                title={<Title text={props?.title} skin="h4" textColor="primary-700" />}
                onClose={handleCancelProc}
                footer={renderFooter()}
                mask={false}
                draggable
                componentClasses={styles.dialogWrapper}
                style={{pointerEvents: 'all', display: dialogZIndex > -1 ? 'block' : 'none'}}
                closeIcon={<Icon icon={PriorityIcon.closeSmall} iconColor="secondary-500" />}
                content={
                    <div tabIndex={-1} ref={dlgContentRef}>
                        <ReportDialogContent
                            config={config}
                            selectedOption={selectedOption}
                            chooseOption={chooseOption}
                            formats={formats}
                            selectedFormat={selectedFormat}
                            setSelectedFormat={setSelectedFormat}
                            excelTemplates={excelTemplates}
                            selectedExcelTemplate={selectedExcelTemplate}
                            setSelectedExcelTemplate={setSelectedExcelTemplate}
                            isLockedExcel={isLockedExcel}
                            setIsLockedExcel={setIsLockedExcel}
                            isPdf={isPdf}
                            setIsPdf={setIsPdf}
                            hasword={props.hasword}
                            closeDialog={onClose}
                        />
                    </div>
                }
                zIndex={dialogZIndex}
            />
        </div>
    );
});
